/**
 * Computes human-readable diagnostics for known cross-field DQ rules by parsing
 * quarantine `input_data` JSON (same values shown in the mapped grid).
 */

import type { DqxContractRuleMeta } from "@/services/dqxContractRulesLookup";

function normalizeFieldKey(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
}

/**
 * Parse a numeric cell value; returns null if missing or not a number.
 */
function parseAmount(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw).trim().replace(/,/g, "");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Read a field from a row object using loose key matching (case, spaces, hyphens).
 */
function getField(row: Record<string, unknown> | null, logical: string): unknown {
  if (!row) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, logical)) return row[logical];
  const want = normalizeFieldKey(logical);
  for (const k of Object.keys(row)) {
    if (normalizeFieldKey(k) === want) return row[k];
  }
  return undefined;
}

function formatMoney(n: number): string {
  return n.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function totalColumnFromMessage(msg: string | undefined): string | undefined {
  if (!msg || typeof msg !== "string") return undefined;
  const m = msg.match(/^\s*([^:]+?)\s+does not reconcile with component fields/i);
  const raw = m?.[1]?.trim();
  return raw ? raw : undefined;
}

function summationDetail(
  input: Record<string, unknown> | null,
  totalColumn: string,
  componentColumns: string[]
): string {
  const total = parseAmount(getField(input, totalColumn));

  let sum = 0;
  const parts: string[] = [];
  for (const field of componentColumns) {
    const v = parseAmount(getField(input, field));
    const n = v ?? 0;
    sum += n;
    parts.push(`${field}: ${formatMoney(n)}`);
  }

  const totalLabel = total === null ? "(missing or invalid)" : formatMoney(total);
  const diff = total === null ? null : Math.abs(total - sum);
  const diffSeg = diff === null ? "" : ` | |Total − sum|: ${formatMoney(diff)} (tolerance ≤ 0.01)`;

  return `${totalColumn}: ${totalLabel} | Sum of components: ${formatMoney(sum)}${diffSeg}. ${parts.join("; ")}.`;
}

export interface QuarantineRuleLike {
  name?: string;
  message?: string;
  columns?: string[];
}

/**
 * Returns an extra line of context for known cross-field rules, or undefined.
 */
export function buildCrossFieldDetail(
  rule: QuarantineRuleLike,
  inputData: Record<string, unknown> | null,
  meta?: DqxContractRuleMeta
): string | undefined {
  const isSummationRule = meta?.rule_type?.trim().toLowerCase() === "summation";
  if (!isSummationRule) return undefined;

  const candidates = [
    ...(meta?.affected_columns ?? []).map((c) => String(c).trim()),
    ...(Array.isArray(rule.columns) ? rule.columns.map((c) => String(c).trim()) : []),
  ].filter(Boolean);
  const uniqueColumns = Array.from(new Set(candidates));
  if (uniqueColumns.length < 2) return undefined;

  const totalFromMessage = totalColumnFromMessage(
    typeof rule.message === "string" ? rule.message : undefined
  );
  const totalColumn =
    (totalFromMessage &&
      uniqueColumns.find((c) => normalizeFieldKey(c) === normalizeFieldKey(totalFromMessage))) ||
    uniqueColumns[0];
  if (!totalColumn) return undefined;

  const componentColumns = uniqueColumns.filter((c) => c !== totalColumn);
  if (componentColumns.length === 0) {
    return undefined;
  }

  return summationDetail(inputData, totalColumn, componentColumns);
}
